import { useQuery } from '@tanstack/react-query';
import { Clock, UserPlus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface PendingInvitesSectionProps {
  companyId: string;
}

export function PendingInvitesSection({ companyId }: PendingInvitesSectionProps) {
  const { data: pendingInvites = [], refetch } = useQuery({
    queryKey: ['pending-invites', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, display_name, created_at')
        .eq('company_id', companyId)
        .eq('sso_provider', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!companyId,
  });

  const handleRemoveInvite = async (profileId: string, email: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', profileId);

      if (error) throw error;
      
      toast.success(`Invitation for ${email} removed`);
      refetch();
    } catch (error: any) {
      toast.error(`Failed to remove invitation: ${error.message}`);
    }
  };

  if (pendingInvites.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-orange-500" />
          Pending Invitations
          <Badge variant="secondary">{pendingInvites.length}</Badge>
        </CardTitle>
        <CardDescription>
          Team members who have been invited but haven't completed WordPress SSO registration
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {pendingInvites.map((invite) => (
            <div
              key={invite.id}
              className="flex items-center justify-between p-3 rounded-lg border border-border bg-card"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <UserPlus className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium text-sm">{invite.display_name}</p>
                  <p className="text-xs text-muted-foreground">{invite.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-orange-500 border-orange-500/50">
                  Pending
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {new Date(invite.created_at).toLocaleDateString()}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => handleRemoveInvite(invite.id, invite.email)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          These users need to sign in via WordPress SSO with the same email address to access the platform.
        </p>
      </CardContent>
    </Card>
  );
}